/**
 * The font-metrics contract the native Control text engine shapes against, and the px quantisation
 * every implementation shares, from design-unit data alone. The whole-pixel advance round above the
 * subpixel threshold carries a remainder, so it lives in `textLayout.ts`. This module imports only
 * `perObject`, so no consumer pulls in Open Sans's baked data.
 */
import { perObject } from '../../../../utils/perObject';
/**
 * `units` scaled to `fontSizePx` as `units * (fontSizePx / unitsPerEm)`, which `units * fontSizePx
 * / unitsPerEm` matches bit for bit only at a power-of-two `unitsPerEm`. No rule stops at this
 * float: ascent and descent ceil to whole pixels, and an advance goes through FreeType's and
 * HarfBuzz's fixed-point chain to whole 1/64 px.
 */
function unitsToPx(units, metrics, fontSizePx) {
    return units * (fontSizePx / metrics.unitsPerEm);
}
/**
 * Ascent rounded up to a whole pixel, as FreeType's 26.6 size metrics
 * (`face->size->metrics.ascender`) are (`modules/text_server_adv/text_server_adv.cpp:1515-1516`).
 * A line's baseline sits this far below its top (`rich_text_label.cpp:1049`), and
 * every baseline-relative quantity adds to this value rather than re-deriving it.
 */
export function getFontAscentPx(metrics, fontSizePx) {
    return Math.ceil(unitsToPx(metrics.ascent, metrics, fontSizePx));
}
/**
 * Pixel line pitch: ascent and descent each ceil to a whole pixel before the sum
 * (`modules/text_server_adv/text_server_adv.cpp:1515-1516`). A float sum rounded once
 * undershoots by about 1px, as measured against Godot. At Open Sans SemiBold 16 with
 * Label's spacing: ceil(2189*16/2048) + ceil(600*16/2048) + 3 = 18 + 5 + 3 = 26.
 */
// `lineSpacingPx` has no default: `Math::round(3 * scale)` is Label's constant
// (`scene/theme/default_theme.cpp:392`), and other controls read another key or none.
export function getFontLinePitchPx(metrics, fontSizePx, lineSpacingPx) {
    const ascentPx = getFontAscentPx(metrics, fontSizePx);
    const descentPx = Math.ceil(unitsToPx(metrics.descent, metrics, fontSizePx));
    return ascentPx + descentPx + lineSpacingPx;
}
/** 1.0 in FreeType's 16.16 fixed point (`FT_Fixed`). */
export const FIXED_16_16_ONE = 65536;
/** 1.0 px in FreeType's 26.6 fixed point (`FT_F26Dot6`). */
export const FIXED_26_6_ONE = 64;
/**
 * `freetype/include/freetype/fttypes.h`'s `FT_DivFix` (`freetype/src/base/ftcalc.c`):
 * `a / b` in 16.16, rounded half up on the magnitude (`q = ( ( (FT_UInt64)a << 16 ) +
 * ( b >> 1 ) ) / b`), then signed. A division, not a shift: the intermediate exceeds 32 bits.
 * A zero divisor gives the largest 32-bit magnitude, signed by `a`, not infinity.
 */
export function ftDivFix(a, b) {
    if (b === 0)
        return a < 0 ? -0x7fffffff : 0x7fffffff;
    const sign = Math.sign(a) * Math.sign(b);
    const magnitude = Math.floor((Math.abs(a) * FIXED_16_16_ONE + Math.floor(Math.abs(b) / 2)) / Math.abs(b));
    return sign * magnitude;
}
/**
 * `freetype/src/base/ftcalc.c`'s `FT_MulDiv`: `a * b / c`, rounded half up on the magnitude. A zero
 * `c` gives the largest 32-bit magnitude, signed by `a * b`.
 * @canonical FreeType's `a * b / c` in integers
 */
export function ftMulDiv(a, b, c) {
    if (c === 0)
        return a * b < 0 ? -0x7fffffff : 0x7fffffff;
    const sign = Math.sign(a) * Math.sign(b) * Math.sign(c);
    return sign * Math.floor((Math.abs(a) * Math.abs(b) + Math.floor(Math.abs(c) / 2)) / Math.abs(c));
}
/** `freetype/src/base/ftcalc.c`'s `FT_MulFix`: `a * b / 65536`, rounded half up on the magnitude. */
export function ftMulFix(a, b) {
    return ftMulDiv(a, b, FIXED_16_16_ONE);
}
/** `FT_PIX_FLOOR`: down to a whole pixel in 26.6. */
export function pixFloor(value) {
    return Math.floor(value / FIXED_26_6_ONE) * FIXED_26_6_ONE;
}
/** `FT_PIX_ROUND`: to the nearest whole pixel in 26.6, half up. */
export function pixRound(value) {
    return pixFloor(value + FIXED_26_6_ONE / 2);
}
/** `FT_PIX_CEIL`: up to a whole pixel in 26.6. */
export function pixCeil(value) {
    return pixFloor(value + FIXED_26_6_ONE - 1);
}
/**
 * The `FT_Size_Request` Godot issues: `FT_SIZE_REQUEST_TYPE_NOMINAL` at `width =
 * height = sz * 64` with zero resolutions (`text_server_adv.cpp:1500-1507`, `sz` at
 * `:1481`, `p_size * 64` at `text_server_adv.h:396-403`). The size API takes
 * `int64_t`, so Godot's shaping size is always whole pixels.
 */
// `freetype/src/base/ftobjs.c:3257-3258,3295-3320`: `x_scale = FT_DivFix(sz * 64, unitsPerEm)`.
// `:3350-3361`: `x_ppem = (sz * 64 + 32) >> 6`, the size rounded to whole pixels,
// which `text_server_adv.cpp:1509` divides by for `fd->scale`.
function freeTypeSizeRequest(unitsPerEm, fontSizePx) {
    // `req.width = sz * 64.0` lands in an `FT_Long`, truncating toward zero.
    const requested26_6 = Math.trunc(fontSizePx * FIXED_26_6_ONE);
    return { xScale: ftDivFix(requested26_6, unitsPerEm), ppem: (requested26_6 + 32) >> 6 };
}
/**
 * The ppem and the 16.16 scale of a TrueType glyph load at `fontSizePx` (`ttobjs.c`,
 * `tt_size_reset`). A font whose `head` flags round the ppem scales by the whole-pixel size,
 * which a whole-pixel request already is.
 */
export function freeTypeTrueTypeSize(unitsPerEm, fontSizePx, isPpemRounded) {
    const { xScale, ppem } = freeTypeSizeRequest(unitsPerEm, fontSizePx);
    return { scale: isPpemRounded ? ftDivFix(ppem * FIXED_26_6_ONE, unitsPerEm) : xScale, ppem };
}
/**
 * FreeType's `x_scale` for `fontSizePx`: the 16.16 factor that takes a design-unit coordinate to 26.6
 * px (`FT_MulFix(units, scale)`), which scales every outline point Godot reads
 * (`text_server_adv.cpp:3613`, `FT_Load_Glyph`).
 */
export function getFontOutlineScale(unitsPerEm, fontSizePx) {
    return freeTypeSizeRequest(unitsPerEm, fontSizePx).xScale;
}
/** `servers/text/text_server.h:172`: the largest font size `SUBPIXEL_POSITIONING_AUTO` still positions subpixel-precisely. */
const SUBPIXEL_POSITIONING_ONE_HALF_MAX_SIZE = 20;
/**
 * Godot's `subpos` (`text_server_adv.cpp:6936`) under the default
 * `SUBPIXEL_POSITIONING_AUTO` (`text_server_adv.h:343`): true at or below 20 px
 * (`servers/text/text_server.h:172`). When false, `text_server_adv.cpp:7079-7084`
 * rounds every advance to a whole pixel, so a Label at 28 has integer pen positions.
 */
// The other disjunct, `scale != 1.0`, never fires for a whole-pixel size: `fd->scale`
// is `sz / y_ppem` (`text_server_adv.cpp:1509`). A fractional size, which Godot's
// `int64_t` API cannot express, takes the subpixel branch.
export function fontUsesSubpixelPositioning(fontSizePx) {
    return !Number.isInteger(fontSizePx) || fontSizePx <= SUBPIXEL_POSITIONING_ONE_HALF_MAX_SIZE;
}
/**
 * A character's advance in px, through FreeType's and HarfBuzz's fixed-point
 * chain, not the continuous scale: Godot's advance is HarfBuzz's `x_advance`
 * (`text_server_adv.cpp:7077`). A missing glyph takes `averageAdvanceUnits`,
 * so the line does not collapse around it.
 */
// 1. `freetype/src/base/ftadvanc.c:52`: `FT_MulFix(1024 * advance, x_scale)` to 16.16, half up.
// 2. `thirdparty/harfbuzz/src/hb-ft.cc:519,523`: `(v + (1<<9)) >> 10` to 26.6, half up.
//    Unhinted (`hb-ft.cc:115`: `FT_LOAD_DEFAULT | FT_LOAD_NO_HINTING`), the raw-`hmtx` path (`ftadvanc.c:124-131`).
// At 2048 units and 16px this is `ceil(units / 2) / 64`: 1/128 px wider for an odd advance.
export function getFontGlyphAdvancePx(metrics, ch, fontSizePx) {
    const units = metrics.getGlyphAdvanceUnits(ch) ?? metrics.averageAdvanceUnits;
    const { xScale } = freeTypeSizeRequest(metrics.unitsPerEm, fontSizePx);
    const advance16_16 = ftMulFix(1024 * units, xScale);
    // The error only adds width, so a run of odd advances can push
    // `shapedTextSizeWidthPx`'s ceil a whole pixel wider, as in Godot.
    const advance26_6 = Math.floor((advance16_16 + 512) / 1024);
    return advance26_6 / FIXED_26_6_ONE;
}
/** Whether the variation changes the glyph outline at all. */
export function isGlyphVaried({ embolden = 0, skew = 0 }) {
    return embolden !== 0 || skew !== 0;
}
/** One string for a variation, with an absent embolden and skew equal to 0: the key of every cache that varies by it. */
export function variationKey({ embolden = 0, skew = 0 }) {
    return `${embolden}/${skew}`;
}
/**
 * The wrappers of one face, by embolden and skew, then by outlines: one each, so a layout memoised on
 * `FontMetrics` identity keeps its hits.
 */
const variationsOf = perObject((_face) => new Map());
/**
 * `metrics` with a `FontVariation`'s `embolden` and `skew` set, and the `outlines` its text follows.
 * The wrapper reads every other field through the face, so a live getter stays live. With neither
 * embolden nor skew and no outlines, it is the face itself. Two fonts that share a face and a variation keep
 * their own outlines, each on its own wrapper.
 */
export function withVariation(metrics, { embolden = 0, skew = 0, outlines }) {
    if (!outlines && !isGlyphVaried({ embolden, skew }))
        return metrics;
    const byVariation = variationsOf(metrics);
    const key = variationKey({ embolden, skew });
    let byOutlines = byVariation.get(key);
    if (!byOutlines)
        byVariation.set(key, (byOutlines = new Map()));
    let wrapped = byOutlines.get(outlines);
    if (!wrapped)
        byOutlines.set(outlines, (wrapped = Object.create(metrics, {
            embolden: { value: embolden },
            skew: { value: skew },
            ...(outlines !== undefined && { outlines: { value: outlines } }),
        })));
    return wrapped;
}
/** `metrics` carrying `outlines`, keeping its own embolden and skew. */
export function withOutlines(metrics, outlines) {
    return (metrics.outlines === outlines
        ? metrics
        : withVariation(metrics, { embolden: metrics.embolden, skew: metrics.skew, outlines }));
}
/**
 * The px an embolden adds to every advance: `embolden * size.x / 4096` with `size.x` in 26.6
 * (`text_server_adv.cpp:3238-3239`), so `embolden * fontSizePx / 64`.
 */
export function getFontExtraAdvancePx(embolden, fontSizePx) {
    return (embolden * fontSizePx) / 64;
}
/** `getFontGlyphAdvancePx` plus the face's own embolden: the advance `get_char_size` reports for a `FontVariation`. */
export function getFontEmboldenedAdvancePx(metrics, ch, fontSizePx) {
    return (getFontGlyphAdvancePx(metrics, ch, fontSizePx) + getFontExtraAdvancePx(metrics.embolden ?? 0, fontSizePx));
}
/**
 * The px `FT_Outline_Embolden` grows an outline by: `strength = embolden * size.x / 16` in 26.6
 * (`text_server_adv.cpp:1314-1315`). The ink keeps its left and bottom edges and gains this much
 * at the right and top.
 */
export function getFontEmboldenGrowthPx(embolden, fontSizePx) {
    return (embolden * fontSizePx) / 16;
}
/**
 * The strength Godot passes to `FT_Outline_Embolden`: `embolden * size.x / 16`, truncated, in 26.6
 * units, with `size.x` the font size in 26.6 (`text_server_adv.cpp:1314`). A float product, as
 * `embolden` is a `float`. `getFontEmboldenGrowthPx` is the same strength before the truncation.
 */
export function getFontEmboldenStrength(embolden, fontSizePx) {
    return Math.trunc(Math.fround(Math.fround(embolden) * fontSizePx * FIXED_26_6_ONE) / 16);
}
/** `metrics.getKerningAdjustmentUnits(a, b)` scaled to `fontSizePx`. `0` short-circuits without a scale. */
export function getFontKerningAdjustmentPx(metrics, a, b, fontSizePx) {
    const units = metrics.getKerningAdjustmentUnits(a, b);
    if (units === 0)
        return 0;
    return unitsToPx(units, metrics, fontSizePx);
}
//# sourceMappingURL=fontMetrics.js.map